import React, { useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@ts-digital/vrc";
import { Input, Select, Tooltip } from "antd";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getParties, deleteParty } from "../../store/actions/parties";
import LoaderSimple from "../general/loader";
import VaporContentSwitcher from "../vapor/contentSwitcher";
import VaporFormItem from "../vapor/form";
import PartiesCard from "./card";
import PartiesDetail from "./detail";
import EmptyParties from "./emptyParties";
import { orderBySingleField } from "../../libs/arrayUtils";
import "./style.css";
import { FormattedMessage, useIntl } from "react-intl";
import { notification } from "antd";
import { RegistryContext } from "../../contexts/RegistryContext";
// import iban from "iban";

const Option = Select.Option;

const letters = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z"
];

const PartiesPage = ({  installments }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const main = useSelector(state => state.main);
  const parties = useSelector(state => state.parties);
  const isGettingParties = useSelector(state =>
    state.parties.getParties.status.started === true &&
      state.parties.getParties.status.error === false);
  const [partiesList, setPartiesList] = useState([]);
  const [categorySelected, setCategorySelected] = useState("customers");
  const [isModalVisible] = useState(false);
  const [orderSelected, setOrderSelected] = useState("az");
  const [letterSelected, setLetterSelected] = useState(" ");
  const [searchValue, setSearchValue] = useState("");
  const [detailOpened, setDetailOpened] = useState(null);
  const [filteredParties, setFilteredParties] = useState([]);
  const [partiesToShow, setPartiesToShow] = useState([]);
  const [limitCards, setLimitCards] = useState(true);
  const isEditingState = useSelector(state => state.parties.editParty.status);
  const isCompletedEditing =
    isEditingState?.ended === true && isEditingState?.error === false;
  const isDeletingState = useSelector(state => state.parties.deleteParty.status);

  const [typeEditing, setTypeEditing] = useState(null);
  const [typeDeleting, setTypeDeleting] = useState(null);

  const mounted = useRef();

  const intl = useIntl();

  const isDeletingSuccess =
    isDeletingState?.started === false &&
    isDeletingState?.ended === true &&
    isDeletingState?.error === false;

  const isDeletingError =
    isDeletingState?.started === false &&
    isDeletingState?.ended === false &&
    isDeletingState?.error === true;

  const isEditingSuccess =
    isEditingState?.started === false &&
    isEditingState?.ended === true &&
    isEditingState?.error === false;

  const isEditingError =
    isEditingState?.started === false &&
    isEditingState?.ended === false &&
    isEditingState?.error === true;

  useEffect(() => {
    if (!mounted.current) {
      // set mounted to true
      mounted.current = true;
    } else {
      // do componentDidUpdate logic
      if (
        typeEditing !== null &&
        (typeEditing === "success" || typeEditing === "error")
      ) {
        openEditNotification(typeEditing);
        setTypeEditing(null);
      }
      if (
        typeDeleting !== null &&
        (typeDeleting === "success" || typeDeleting === "error")
      ) {
        openDeleteNotification(typeDeleting);
        setTypeDeleting(null);
      }
    }
  }, [typeEditing, typeDeleting]);

  useEffect(() => {
    dispatch(getParties(
      auth, main.companyUuidSelected, 100, "asc"
    ));
  }, [isModalVisible, isCompletedEditing]);

  useEffect(() => {
    if (parties.getParties.data) {
      setPartiesList(parties?.getParties?.data?.results);
    }
  }, [parties]);

  useEffect(() => {
    setFilteredParties(partiesList);
  }, [partiesList]);

  useEffect(() => {
    const customers = partiesList?.filter(p =>
      p.relationships.includes("CUSTOMER"));
    const suppliers = partiesList?.filter(p =>
      p.relationships.includes("SUPPLIER"));
    const customersAlphabeticallyOrdered = orderBySingleField(customers,
      "description");
    const customersAlphabeticallyOrderedReverse = orderBySingleField(
      customers,
      "description",
      false
    );
    const suppliersAlphabeticallyOrdered = orderBySingleField(suppliers,
      "description");
    const suppliersAlphabeticallyOrderedReverse = orderBySingleField(
      suppliers,
      "description",
      false
    );
    setFilteredParties(categorySelected === "customers" && orderSelected === "az"
      ? customersAlphabeticallyOrdered
      : categorySelected === "customers" && orderSelected === "za"
        ? customersAlphabeticallyOrderedReverse
        : categorySelected === "company" && orderSelected === "az"
          ? suppliersAlphabeticallyOrdered
          : suppliersAlphabeticallyOrderedReverse);
  }, [partiesList, categorySelected, orderSelected]);

  useEffect(() => {
    const searchedParties = filteredParties.filter(p =>
      p.description.toLowerCase().includes(searchValue.toLowerCase()));
    const partiesByLetter = searchedParties.filter(p =>
      p.description.charAt(0).toLowerCase() === letterSelected.toLowerCase());
    setPartiesToShow(letterSelected !== " " ? partiesByLetter : searchedParties);
  }, [letterSelected, searchValue, filteredParties]);

  const registryStore = useContext(RegistryContext);

  const deletePartyCard = (partyId, latestVersion) => {
    dispatch(deleteParty(
      auth, main.companyUuidSelected, partyId, latestVersion
    )).then(res => {
      setTypeDeleting(isDeletingState.error === false ? "success" : "error");
      dispatch(getParties(
        auth, main.companyUuidSelected, 100, "asc"
      ));
    });
  };

  const feedbackMessageSuccess = useIntl().formatMessage({
    id: "parties.delete.feedbackMessageSuccess"
  });
  const feedbackMessageError = useIntl().formatMessage({
    id: "parties.delete.feedbackMessageError"
  });
  const feedbackDescriptionError = useIntl().formatMessage({
    id: "parties.delete.feedbackDescriptionError"
  });

  const feedbackMessageEditSuccess = useIntl().formatMessage({
    id: "parties.edit.feedbackMessageSuccess"
  });
  const feedbackMessageEditError = useIntl().formatMessage({
    id: "parties.edit.feedbackMessageError"
  });
  const feedbackDescriptionEditError = useIntl().formatMessage({
    id: "parties.edit.feedbackDescriptionError"
  });

  const openDeleteNotification = type => {
    notification[type]({
      message: isDeletingSuccess
        ? feedbackMessageSuccess
        : feedbackMessageError,
      description: isDeletingError ? feedbackDescriptionError : "",
      className: isDeletingSuccess
        ? "success-create-party"
        : "error-create-party"
    });
  };

  const openEditNotification = type => {
    notification[type]({
      message: isEditingSuccess
        ? feedbackMessageEditSuccess
        : feedbackMessageEditError,
      description: isEditingError ? feedbackDescriptionEditError : "",
      className: isEditingSuccess
        ? "success-create-party"
        : "error-create-party"
    });
  };

  const editSearchValue = e => {
    setSearchValue(e.target.value);
  };

  const goToCreateNewPage = () => {
    navigate("/create-contact");
  };

  const renderLetters = () => {
    let list = [];
    letters.map(letter => {
      return list.push(<div
        id={"dbx-parties-filter-letter-" + letter}
        key={"dbx-parties-filter-letter-" + letter}
        name={"dbx-parties-filter-letter-" + letter}
        className={
          "dbx-parties-filter-letters-letter" +
            (letterSelected === letter ? " active" : "")
        }
        onClick={() => {
          setLetterSelected(letter === letterSelected ? " " : letter);
        }}
      >
        {letter}
      </div>);
    });
    return list;
  };

  const renderList = () => {
    let list = [];
    if (partiesToShow) {
      partiesToShow.map((address, index) => {
        if (!limitCards || (limitCards && index < 6)) {
          return list.push(<PartiesCard
            key={"dbx-parties-card_" + address.id}
            detailOpened={detailOpened}
            setDetailOpened={setDetailOpened}
            address={address}
            deletePartyCard={deletePartyCard}
          />);
        }
        return null;
      });
      return list;
    }
    return undefined;
  };

  const renderHeader = partiesExists => {
    return (
      <div
        className={"dbx-parties-header" + (!partiesExists ? " noParties" : "")}
      >
        <div className="dbx-parties-header-category">
          <VaporContentSwitcher
            selected={categorySelected}
            itemsWidth={140}
            onChange={setCategorySelected}
            items={[
              { value: "customers", icon: "male", label: intl.formatMessage({ id: "addressBook.customers"}) },
              { value: "company", icon: "truck", label: intl.formatMessage({ id: "addressBook.suppliers"}) }
            ]}
          />
        </div>
        {partiesExists && (
          <div className="dbx-parties-header-add">
            <Tooltip color={"rgb(0, 80, 117)"}
              title="Aggiungi nuovo">
              <div
                className="TS-button-circle"
                onClick={() => goToCreateNewPage()}
              >
                <FontAwesomeIcon
                  icon={["far", "plus"]}
                  style={{
                    fontSize: "23px",
                    color: "white",
                    margin: "0 auto"
                  }}
                />
              </div>
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  const renderHeaderAndFilters = () => {
    return (
      <>
        {renderHeader(partiesToShow?.length > 0)}
        <div className="dbx-parties-filter">
          <div className="dbx-parties-filter-row">
            <div className="dbx-parties-filter-fulltext">
              <VaporFormItem label="Cerca nella tua rubrica">
                <Input
                  value={searchValue}
                  onChange={e => editSearchValue(e)}
                  placeholder="Cerca nella rubrica"
                  suffix={
                    <Button style={{ marginRight: -8 }}>
                      <FontAwesomeIcon icon={["far", "search"]} />
                    </Button>
                  }
                />
              </VaporFormItem>
            </div>
            <div className="dbx-parties-filter-order">
              <VaporFormItem label=" ">
                <Select
                  value={orderSelected}
                  onChange={e => setOrderSelected(e)}
                >
                  <Option value={"az"}>Ordina per A-Z</Option>
                  <Option value={"za"}>Ordina per Z-A</Option>
                </Select>
              </VaporFormItem>
            </div>
          </div>
          <div className="dbx-parties-filter-row">
            <div className="dbx-parties-filter-letters">{renderLetters()}</div>
          </div>
        </div>
        <div className="dbx-parties-list">
          {(!partiesList || partiesList?.length === 0) &&
            !parties.getParties.status.started &&
            parties.getParties.status.ended && (
            <div className="dbx-parties-list-empty">
                Nessun contatto trovato
            </div>
          )}
          {isGettingParties ? (
            <div className="dbx-parties-list-loader">
              <LoaderSimple />
            </div>
          ) : !isGettingParties && partiesToShow?.length > 0 ? (
            renderList()
          ) : !isGettingParties && partiesToShow.length === 0 ? (
            <FormattedMessage id="parties.noPartyFound" />
          ) : (
            <div>ciao</div>
          )}
          {detailOpened && (
            <PartiesDetail
              registryStore={registryStore}
              address={detailOpened}
              onClose={setDetailOpened}
              setTypeEditing={setTypeEditing}
              isEditingState={isEditingState}
              installments={installments}
            />
          )}
        </div>
        <div className="dbx-parties-view-more-button-container">
          <Button
            kind="secondary"
            onClick={() => setLimitCards(!limitCards)}
            size="medium"
            className="dbx-parties-view-more-button"
            style={{ display: partiesToShow.length > 6 ? "" : "none" }}
          >
            <FormattedMessage
              id={limitCards ? "parties.viewMoreButton" : "parties.hideButton"}
            />
          </Button>
        </div>
      </>
    );
  };

  return (
    <>
      <div className="dbx-parties-root">
        {!isGettingParties && partiesToShow?.length > 0 ? (
          renderHeaderAndFilters()
        ) : isGettingParties ? (
          <div className="dbx-parties-list-loading">
            <LoaderSimple />
          </div>
        ) : !isGettingParties &&
          partiesToShow.length === 0 &&
          searchValue === "" &&
          letterSelected === " " ? (
            <>
              {renderHeader(partiesToShow?.length > 0)}
              <EmptyParties
                emptyLabel={intl.formatMessage({id: "addressBook.empty"})}
                onButtonClick={() => goToCreateNewPage()}
              />
            </>
          ) : (
            renderHeaderAndFilters()
          )}
      </div>
    </>
  );
};

export default PartiesPage;
